import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, Loader2 } from "lucide-react";
import { login, setToken, formatApiErrorDetail } from "@/lib/mastroAuth";
import { MastroBrand } from "@/components/MastroBrand";
import { LOGIN_FORM as LOGIN } from "@/constants/testIds";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const deletionRequested = Boolean(location.state?.deletionRequested);
  const [number, setNumber] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!number.trim() || !password) {
      setError("Inserisci numero WhatsApp e password.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await login(number.trim(), password, remember);
      setToken(data.access_token);
      navigate("/app", { replace: true });
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setError("Numero o password non validi.");
      } else if (status === 422) {
        setError("Controlla i dati inseriti e riprova.");
      } else {
        setError(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "Si è verificato un errore. Riprova."
        );
      }
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={LOGIN.page}
      className="min-h-screen w-full bg-neutral-50 text-neutral-950 flex flex-col"
    >
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col px-6 py-10">
        <div>
          <MastroBrand className="h-10" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mt-10 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm"
        >
          <h1 className="text-2xl font-bold tracking-tight">Accedi a Mastro</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Usa il numero WhatsApp verificato e la tua password.
          </p>

          {deletionRequested && (
            <div
              data-testid={LOGIN.deletionRequestedBanner}
              className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              role="status"
            >
              Richiesta di eliminazione registrata. L’account è disabilitato. La cancellazione definitiva verrà completata tramite procedura amministrativa.
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            <div>
              <label className="text-sm font-medium text-neutral-700" htmlFor="number">
                Numero WhatsApp
              </label>
              <input
                id="number"
                type="tel"
                inputMode="tel"
                data-testid={LOGIN.numberInput}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="+39 333 000 0000"
                className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none transition-colors placeholder:text-neutral-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                data-testid={LOGIN.passwordInput}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="La tua password"
                className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none transition-colors placeholder:text-neutral-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <p data-testid={LOGIN.formError} className="text-sm font-medium text-red-600">
                {error}
              </p>
            )}

            <label className="flex items-center gap-2 text-sm text-neutral-700 select-none cursor-pointer">
              <input
                type="checkbox"
                data-testid={LOGIN.rememberCheckbox}
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-neutral-300 text-teal-600 focus:ring-teal-500/30"
              />
              Rimani collegato
            </label>

            <button
              type="submit"
              data-testid={LOGIN.submitButton}
              disabled={submitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Accesso…
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4" />
                  Accedi
                </>
              )}
            </button>
          </form>
        </motion.div>
        <p className="mt-8 text-xs text-neutral-400">
          Il tuo assistente operativo su WhatsApp.
        </p>
      </div>
    </div>
  );
}
